import { useState } from 'react';
import { useTranslation } from '../../../../i18n';
import { Modal, ModalFooter } from '../../../../components/Shared/Modal';
import { FormField } from '../../../../components/Shared/FormField';

interface GenerateMonthlyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (year: number, month: number) => Promise<void>;
  loading?: boolean;
}

function defaultYearMonth(): { year: number; month: number } {
  const now = new Date();
  // Default = previous month (Vormonat)
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 };
}

export function GenerateMonthlyModal({
  isOpen,
  onClose,
  onSubmit,
  loading,
}: GenerateMonthlyModalProps) {
  const { t } = useTranslation(['admin', 'common']);
  const initial = defaultYearMonth();
  const [year, setYear] = useState<number>(initial.year);
  const [month, setMonth] = useState<number>(initial.month);

  const handleConfirm = async () => {
    await onSubmit(year, month);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('admin:tenantInvoices.generateModal.title')}
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleConfirm}
          confirmText={t('admin:tenantInvoices.generateModal.submit')}
          cancelText={t('common:actions.cancel')}
          loading={loading}
        />
      }
    >
      <div className="space-y-3">
        <p className="text-[13px] text-slate-600">
          {t('admin:tenantInvoices.generateModal.description')}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('admin:tenantInvoices.generateModal.year')}>
            <input
              type="number"
              min={2024}
              max={2100}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            />
          </FormField>
          <FormField label={t('admin:tenantInvoices.generateModal.month')}>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {String(m).padStart(2, '0')}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      </div>
    </Modal>
  );
}
